import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import { useMusic } from './hooks/useMusic';
import {
  NEGARA_OUTRO_ACTIVE_CLASS,
  TARGETING_CURSOR_SUPPRESS_CLASS,
} from './config/cursorConfig';
import './ExploreUnknownNegaraOutro.css';

const SLIDE_COUNT = 5;
const WHITE_FADE_MS = 2000;

/**
 * @param {unknown} raw
 * @returns {Array<{ speaker: string, text: string }>}
 */
function normalizeSlideBeats(raw) {
  if (!Array.isArray(raw)) {
    if (typeof raw === 'string' && raw.trim()) {
      return [{ speaker: '', text: raw.trim() }];
    }
    return [];
  }
  return raw
    .map((entry) => {
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        const speaker = String(entry.speaker ?? '').trim();
        const text = String(entry.text ?? entry.line ?? '').trim();
        if (!text) return null;
        return { speaker, text };
      }
      if (typeof entry === 'string' && entry.trim()) {
        return { speaker: '', text: entry.trim() };
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * @param {(key: string, opts?: object) => unknown} t
 * @returns {Array<{ image: number, speaker: string, text: string }>}
 */
export function buildNegaraOutroSteps(t) {
  const steps = [];
  for (let image = 1; image <= SLIDE_COUNT; image += 1) {
    const beats = normalizeSlideBeats(
      t(`exploreUnknownPage.negaraOutro.slide${image}`, { returnObjects: true })
    );
    for (const beat of beats) {
      steps.push({ image, speaker: beat.speaker, text: beat.text });
    }
  }
  if (steps.length === 0) {
    throw new Error('ExploreUnknownNegaraOutro: no outro steps configured');
  }
  return steps;
}

/**
 * Post-battle Negara reunion slideshow (peaceful win with unit 402).
 *
 * @param {{ onComplete: () => void }} props
 */
export default function ExploreUnknownNegaraOutro({ onComplete }) {
  const { t } = useTranslation();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const steps = useMemo(() => buildNegaraOutroSteps(t), [t]);
  const [stepIndex, setStepIndex] = useState(0);
  /** @type {'dialogue' | 'finale' | 'white'} */
  const [phase, setPhase] = useState('dialogue');
  const healPlayedRef = useRef(false);

  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex >= steps.length - 1;

  useMusic('/music/somber_3.mp3', {
    enabled: phase === 'dialogue' || phase === 'finale',
    loop: true,
  });

  const handleContinue = () => {
    if (phase !== 'dialogue') return;
    if (!isLastStep) {
      setStepIndex((i) => i + 1);
      return;
    }
    if (!healPlayedRef.current) {
      healPlayedRef.current = true;
      playSoundEffect('/sounds/negara_heal.mp3');
    }
    setPhase('finale');
    window.setTimeout(() => setPhase('white'), WHITE_FADE_MS);
  };

  const handleWhiteClick = useCallback(() => {
    if (phase !== 'white') return;
    onCompleteRef.current();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'white') return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleWhiteClick();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, handleWhiteClick]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    root.classList.remove(TARGETING_CURSOR_SUPPRESS_CLASS);
    root.classList.add(NEGARA_OUTRO_ACTIVE_CLASS);
    return () => {
      root.classList.remove(NEGARA_OUTRO_ACTIVE_CLASS);
    };
  }, []);

  const showDialogueChrome = phase === 'dialogue';
  const imageIndex = currentStep?.image ?? steps[steps.length - 1]?.image ?? 5;

  const root = (
    <div
      className={[
        'eu-negara-outro',
        phase === 'finale' ? 'eu-negara-outro--finale' : '',
        phase === 'white' ? 'eu-negara-outro--white' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={t('exploreUnknownPage.negaraOutro.regionLabel')}
    >
      <img
        className="eu-negara-outro-image"
        src={`/images/negaras${imageIndex}.jpg`}
        alt=""
      />
      {showDialogueChrome ? (
        <div className="eu-negara-outro-cinematic">
          <div className="eu-negara-outro-subtitle-block">
            {currentStep?.speaker ? (
              <p className="eu-negara-outro-speaker">{currentStep.speaker}</p>
            ) : null}
            <p className="eu-negara-outro-line">{currentStep?.text}</p>
          </div>
          <button type="button" className="eu-negara-outro-continue" onClick={handleContinue}>
            {t('exploreUnknownPage.negaraOutro.continue')}
          </button>
        </div>
      ) : null}
      <div
        className="eu-negara-outro-white-veil"
        role={phase === 'white' ? 'button' : undefined}
        tabIndex={phase === 'white' ? 0 : undefined}
        aria-label={phase === 'white' ? t('exploreUnknownPage.negaraOutro.whiteContinue') : undefined}
        onClick={handleWhiteClick}
        onKeyDown={(e) => {
          if (phase === 'white' && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleWhiteClick();
          }
        }}
      />
    </div>
  );

  if (typeof document === 'undefined') {
    return root;
  }
  return createPortal(root, document.body);
}
